import { Info } from 'lucide-react';
import {
  type Creature,
  type CreatureStatblockItem,
  type CreatureStatblockSpellcastingItem,
  type CreatureStatblockStrike,
} from '@/api/client';
import { useTranslation } from '@/i18n';
import { usePF2eTranslation } from '@/hooks/usePF2eTranslation';
import { Pf2eBadge } from './ui';
import SourceBookCard from './CharacterWizard/SourceBookCard';
import OrcRichText from './OrcRichText';
import type { OrcKnowledgeLinkTarget } from './OrcKnowledgeModalContext';
import type { OrcRichTextRenderContext } from '../utils/orcRichText';
import './CreatureStatblockView.css';

function signed(value: number | null | undefined) {
  if (typeof value !== 'number') return '—';
  return value >= 0 ? `+${value}` : String(value);
}

export function CreatureStatblockView({
  creature,
  t,
  onOrcLinkClick,
}: {
  creature: Creature;
  t: ReturnType<typeof useTranslation>;
  onOrcLinkClick?: (target: OrcKnowledgeLinkTarget) => void;
}) {
  const { tTrait } = usePF2eTranslation();
  const block = creature.statblock?.data;
  if (!block) {
    const variantChoiceRequired = creature.statblock_variants.length > 0;
    return (
      <div className="enc-detail-empty">
        <Info size={20} aria-hidden="true" />
        <p>
          {variantChoiceRequired
            ? t(
              'encounter.detail.variantHint',
              {},
              'This creature is a template with mutually exclusive variants. No variant is selected automatically.',
            )
            : t(
              'encounter.detail.unavailable',
              {},
              'A rich internal statblock is not available yet. Basic combat values are shown below.',
            )}
        </p>
        <RecallKnowledge creature={creature} t={t} />
        <BasicDefense creature={creature} t={t} />
      </div>
    );
  }
  const defenses = block.defenses;
  const abilities = block.abilities || {};
  const skills = block.skills || {};
  const speed = block.speed;
  const source = creature.statblock!;
  const creatureRichTextContext: OrcRichTextRenderContext = {
    level: creature.level,
    actor: { level: creature.level },
  };
  return (
    <article className="enc-statblock">
      {(block.overview?.traits || []).length ? (
        <div className="enc-statblock__traits" aria-label={t('knowledge.modal.traits', {}, 'Traits')}>
          {(block.overview?.traits || []).map((trait) => (
            <Pf2eBadge key={trait} trait={trait}>{tTrait(trait)}</Pf2eBadge>
          ))}
        </div>
      ) : null}
      <RecallKnowledge creature={creature} t={t} />
      {block.overview?.blurb ? (
        <OrcRichText
          content={block.overview.blurb}
          html
          context={creatureRichTextContext}
          onOrcLinkClick={onOrcLinkClick}
        />
      ) : null}
      <section className="enc-statblock__section">
        <p>
          <strong>{t('encounter.detail.perception', {}, 'Perception')}</strong>{' '}
          {signed(block.perception?.modifier)}
          {block.perception?.details ? `; ${block.perception.details}` : ''}
        </p>
        {(block.languages?.values?.length || block.languages?.details) ? (
          <p>
            <strong>{t('encounter.detail.languages', {}, 'Languages')}</strong>{' '}
            {[...(block.languages?.values || []), block.languages?.details || ''].filter(Boolean).join(', ')}
          </p>
        ) : null}
        {Object.keys(skills).length ? (
          <p>
            <strong>{t('encounter.detail.skills', {}, 'Skills')}</strong>{' '}
            {Object.entries(skills).map(([name, value]) => `${name} ${signed(value)}`).join(', ')}
          </p>
        ) : null}
        <p className="enc-statblock__abilities">
          {(['str', 'dex', 'con', 'int', 'wis', 'cha'] as const).map((ability) => (
            <span key={ability}>
              <strong>{t(`encounter.ability.${ability}`, {}, ability.toUpperCase())}</strong>{' '}
              {signed(abilities[ability])}
            </span>
          ))}
        </p>
      </section>
      <section className="enc-statblock__section enc-statblock__defenses">
        <BasicDefense creature={creature} t={t} />
        {defenses?.immunities?.length ? (
          <DefenseLine
            label={t('encounter.detail.immunities', {}, 'Immunities')}
            values={defenses.immunities.map((entry) => entry.type)}
          />
        ) : null}
        {defenses?.weaknesses?.length ? (
          <DefenseLine
            label={t('encounter.detail.weaknesses', {}, 'Weaknesses')}
            values={defenses.weaknesses.map((entry) => `${entry.type} ${entry.value ?? ''}`.trim())}
          />
        ) : null}
        {defenses?.resistances?.length ? (
          <DefenseLine
            label={t('encounter.detail.resistances', {}, 'Resistances')}
            values={defenses.resistances.map((entry) => `${entry.type} ${entry.value ?? ''}`.trim())}
          />
        ) : null}
      </section>
      {speed ? (
        <p>
          <strong>{t('encounter.detail.speed', {}, 'Speed')}</strong>{' '}
          {[
            speed.land ? t('encounter.detail.feet', { value: speed.land }, '{{value}} ft.') : '',
            ...(speed.other || []).map((entry) => (
              `${entry.type || ''} ${t('encounter.detail.feet', { value: entry.value || '' }, '{{value}} ft.')}`.trim()
            )),
          ].filter(Boolean).join(', ')}
        </p>
      ) : null}
      <StatblockItems creatureLevel={creature.level} t={t} onOrcLinkClick={onOrcLinkClick} title={t('encounter.detail.strikes', {}, 'Strikes')} items={block.strikes || []} />
      <StatblockItems creatureLevel={creature.level} t={t} onOrcLinkClick={onOrcLinkClick} title={t('encounter.detail.spellcasting', {}, 'Spellcasting')} items={block.spellcasting || []} />
      <StatblockItems creatureLevel={creature.level} t={t} onOrcLinkClick={onOrcLinkClick} title={t('encounter.detail.actions', {}, 'Abilities')} items={block.actions || []} />
      {block.overview?.public_notes ? (
        <section className="enc-statblock__section">
          <OrcRichText
            content={block.overview.public_notes}
            html
            context={creatureRichTextContext}
            onOrcLinkClick={onOrcLinkClick}
          />
        </section>
      ) : null}
      <footer className="enc-statblock__source">
        <SourceBookCard
          sourceBook={source.source_book}
          sourcePg={source.source_page}
        />
      </footer>
    </article>
  );
}

function RecallKnowledge({
  creature,
  t,
}: {
  creature: Creature;
  t: ReturnType<typeof useTranslation>;
}) {
  const recall = creature.recall_knowledge;
  const skillNames = recall.skills.length
    ? recall.skills.map((skill) => t(`pf2e.skills.${skill}`, {}, skill)).join(', ')
    : t(
      'encounter.detail.recallSkillsAny',
      {},
      'Use an appropriate identification skill chosen by the GM',
    );
  return (
    <section className="enc-statblock__recall" aria-labelledby="enc-recall-knowledge-title">
      <h3 id="enc-recall-knowledge-title">
        {t('encounter.detail.recallKnowledge', {}, 'Recall Knowledge')}
      </h3>
      <p>
        <strong>{t('encounter.detail.recallSkills', {}, 'Skills')}</strong>{' '}
        {skillNames}
      </p>
      <div className="enc-statblock__recall-dcs">
        <span>
          {t('encounter.detail.standardDc', {}, 'Standard DC')}{' '}
          <strong>{recall.standard.dc}</strong>
        </span>
        <span>
          {t('encounter.detail.applicableLoreEasy', {}, 'Applicable Lore (easy)')}{' '}
          <strong>{recall.lore_easy.dc}</strong>
        </span>
        <span>
          {t(
            'encounter.detail.applicableLoreVeryEasy',
            {},
            'Applicable Lore (very easy)',
          )}{' '}
          <strong>{recall.lore_very_easy.dc}</strong>
        </span>
      </div>
      <p className="enc-statblock__meta">
        {t(
          'encounter.detail.recallFameNote',
          {},
          'The GM may adjust the DC for the creature’s fame; that adjustment is not applied automatically.',
        )}
      </p>
    </section>
  );
}

function BasicDefense({
  creature,
  t,
}: {
  creature: Creature;
  t: ReturnType<typeof useTranslation>;
}) {
  return (
    <p>
      <strong>{t('pf2e.armorClass.short', {}, 'AC')}</strong> {creature.ac};{' '}
      <strong>{t('encounter.detail.fortitude', {}, 'Fortitude')}</strong> {creature.fortitude},{' '}
      <strong>{t('encounter.detail.reflex', {}, 'Reflex')}</strong> {creature.reflex},{' '}
      <strong>{t('encounter.detail.will', {}, 'Will')}</strong> {creature.will};{' '}
      <strong>{t('pf2e.hitPoints.short', {}, 'HP')}</strong> {creature.hp}
    </p>
  );
}

function DefenseLine({ label, values }: { label: string; values: string[] }) {
  return <p><strong>{label}</strong> {values.join(', ')}</p>;
}

function StatblockItems({
  creatureLevel,
  t,
  onOrcLinkClick,
  title,
  items,
}: {
  creatureLevel: number;
  t: ReturnType<typeof useTranslation>;
  onOrcLinkClick?: (target: OrcKnowledgeLinkTarget) => void;
  title: string;
  items: Array<
    CreatureStatblockItem
    | CreatureStatblockSpellcastingItem
    | CreatureStatblockStrike
  >;
}) {
  if (!items.length) return null;
  return (
    <section className="enc-statblock__section">
      <h3>{title}</h3>
      {items.map((item, index) => {
        const strike = 'bonus' in item ? item : null;
        const spell = 'entry_id' in item ? item : null;
        const effectiveRank = spell?.heightened_rank ?? spell?.rank;
        const actionGlyph = item.action_type === 'reaction'
          ? '↺'
          : item.action_type === 'free'
            ? '◇'
            : item.actions
              ? '◆'.repeat(item.actions)
              : '';
        return (
          <div key={item.id || `${item.name}-${index}`} className="enc-statblock__item">
            <p>
              <strong>{item.name}</strong>
              {strike ? ` ${signed(strike.bonus)}` : ''}
              {actionGlyph ? ` ${actionGlyph}` : ''}
              {item.traits.length ? ` (${item.traits.join(', ')})` : ''}
            </p>
            {spell?.type === 'spellcastingEntry' ? (
              <p className="enc-statblock__meta">
                {[spell.tradition, spell.preparation].filter(Boolean).join(' · ')}
                {typeof spell.dc === 'number'
                  ? ` · ${t('encounter.detail.spellDc', {}, 'DC')} ${spell.dc}`
                  : ''}
                {typeof spell.attack === 'number'
                  ? ` · ${t('encounter.detail.spellAttack', {}, 'attack')} ${signed(spell.attack)}`
                  : ''}
              </p>
            ) : null}
            {spell?.type === 'spell' ? (
              <p className="enc-statblock__meta">
                {typeof effectiveRank === 'number'
                  ? `${t('encounter.detail.spellRank', {}, 'Rank')} ${effectiveRank}`
                  : ''}
                {spell.at_will
                  ? ` · ${t('encounter.detail.atWill', {}, 'at will')}`
                  : ''}
                {spell.constant
                  ? ` · ${t('encounter.detail.constant', {}, 'constant')}`
                  : ''}
                {spell.unassigned
                  ? ` · ${t('encounter.detail.embeddedSpell', {}, 'embedded ability')}`
                  : ''}
                {spell.ritual
                  ? ` · ${t('encounter.detail.ritual', {}, 'ritual')}`
                  : ''}
                {spell.uses?.max && !spell.at_will
                  ? ` · ${t(
                    'encounter.detail.uses',
                    { value: spell.uses.max },
                    '{{value}} uses',
                  )}`
                  : ''}
              </p>
            ) : null}
            {spell?.type === 'spell' && (
              spell.time
              || spell.range_text
              || spell.target
              || spell.area
              || spell.duration
              || spell.defense
            ) ? (
              <dl className="enc-statblock__facts">
                {spell.time ? <Fact label={t('encounter.detail.cast', {}, 'Cast')} value={spell.time} /> : null}
                {spell.range_text ? <Fact label={t('encounter.detail.range', {}, 'Range')} value={spell.range_text} /> : null}
                {spell.target ? <Fact label={t('encounter.detail.target', {}, 'Target')} value={spell.target} /> : null}
                {spell.area ? (
                  <Fact
                    label={t('encounter.detail.area', {}, 'Area')}
                    value={`${spell.area.value ?? ''} ${spell.area.type}`.trim()}
                  />
                ) : null}
                {spell.duration ? <Fact label={t('encounter.detail.duration', {}, 'Duration')} value={spell.duration} /> : null}
                {spell.defense ? (
                  <Fact
                    label={t('encounter.detail.defense', {}, 'Defense')}
                    value={[
                      spell.defense.basic
                        ? t('encounter.detail.basic', {}, 'basic')
                        : '',
                      spell.defense.statistic || '',
                    ].filter(Boolean).join(' ')}
                  />
                ) : null}
              </dl>
            ) : null}
            {strike?.damage.length ? (
              <p>{strike.damage.map((damage) => `${damage.formula} ${damage.type}`).join(' + ')}</p>
            ) : null}
            {spell?.damage?.length ? (
              <p>{spell.damage.map((damage) => `${damage.formula} ${damage.type}`).join(' + ')}</p>
            ) : null}
            {item.frequency?.max ? (
              <p className="enc-statblock__meta">
                {t('encounter.detail.frequency', {}, 'Frequency')}: {item.frequency.max}
                {item.frequency.per ? ` / ${item.frequency.per}` : ''}
              </p>
            ) : null}
            {item.trigger ? (
              <p><strong>{t('encounter.detail.trigger', {}, 'Trigger')}</strong> {item.trigger}</p>
            ) : null}
            {item.requirements ? (
              <p><strong>{t('encounter.detail.requirements', {}, 'Requirements')}</strong> {item.requirements}</p>
            ) : null}
            {(item.auras || []).map((aura, auraIndex) => (
              <p key={`${aura.slug || 'aura'}-${auraIndex}`} className="enc-statblock__meta">
                {t('encounter.detail.aura', {}, 'Aura')} {aura.radius
                  ? t('encounter.detail.feet', { value: aura.radius }, '{{value}} ft.')
                  : ''}
                {aura.traits.length ? ` (${aura.traits.join(', ')})` : ''}
              </p>
            ))}
            {item.description ? (
              <OrcRichText
                content={item.description}
                html
                context={{
                  level: creatureLevel,
                  actor: { level: creatureLevel },
                  ...(typeof effectiveRank === 'number'
                    ? { item: { rank: effectiveRank } }
                    : {}),
                }}
                onOrcLinkClick={onOrcLinkClick}
              />
            ) : null}
          </div>
        );
      })}
    </section>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}
